import { useSession, useUser } from '@youversion/next-auth-react';
import { usePathname } from 'next/navigation';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import AmplitudeEngagement from '@/components/AmplitudeEngagement';
import { OrganizationAccessGuard } from '@/components/auth/OrganizationAccessGuard';
import { UploadToast } from '@/components/UploadToast';
import { OrganizationProvider } from '@/context/organization';
import { useLocalizedSignInLink } from '@/hooks/locales';
import { useSiteDataBlocked } from '@/hooks/use-site-data-blocked/use-site-data-blocked';
import { setUser } from '@/lib/sentry';
import { MAINTENANCE_MODE, SENTRY_DSN } from '@/utils/env';
import { errorToast } from '@/utils/toast';

import Analytics from '../Analytics';
import { FullPageLoader } from '../loaders/full-page-loader';
import { MaintenanceMode } from '../maintenance/Maintenance';

const shouldNotRedirectRoutes = Object.freeze(['/authenticate']);

interface Props {
  children: React.ReactNode;
}

const maintenanceModeEnabled = MAINTENANCE_MODE === 'on';

/**
 * Checks that the user is authenticated/authorized before rendering the page.
 * If not, redirect to login with route preservation.
 *
 * @example
 * If the app is authenticated, this will enable internal routes, app navigation, and organization context.
 * ```
 * // In `_app.tsx`.
 * <AuthenticatedPage>
 *   <Component {...pageProps} />
 * </AuthenticatedPage>
 * ```
 */
export default function AuthenticatedPage({ children }: Props) {
  const { session, isLoading: isSessionLoading, error: sessionError } = useSession();
  const { user, isLoading: isUserLoading, error } = useUser();
  const pathname = usePathname();
  const { t } = useTranslation();

  // session starts as null with isLoading: false (lazy provider).
  // Treat "null session + not loading" on first render as still initializing.
  const [hasAttemptedFetch, setHasAttemptedFetch] = React.useState(false);
  const [wasSignedIn, setWasSignedIn] = React.useState(false);
  const siteDataBlocked = useSiteDataBlocked();

  // fetchSession sets sessionError even when the fetch was only aborted (e.g. Strict Mode's
  // mount/cleanup/remount cycle) rather than genuinely failed — an abort isn't a real answer.
  const isGenuineSessionError = Boolean(sessionError && sessionError.name !== 'AbortError');

  React.useEffect(() => {
    // A fast-failing fetch can settle (loading -> error) within one React commit, so
    // isSessionLoading may never render as true. sessionError is a durable signal that a
    // fetch was attempted, even when the transient loading state gets coalesced away.
    if (isSessionLoading || session || isGenuineSessionError) {
      setHasAttemptedFetch(true);
    }
  }, [isSessionLoading, session, isGenuineSessionError]);

  const isSignedIn = Boolean(session?.accessToken);

  // Track if the user was ever signed in — prevents redirect during cache refetches
  React.useEffect(() => {
    if (isSignedIn) {
      setWasSignedIn(true);
    }
  }, [isSignedIn]);

  const isLoading = isSessionLoading || isUserLoading || !hasAttemptedFetch;
  const isError = Boolean(error);

  const isAuthenticatePage = Boolean(pathname === '/authenticate' || pathname === '/api/authenticate');

  // Only redirect if we've fetched and never had a session. Once signed in,
  // don't redirect on cache refetches (session may be momentarily null).
  const shouldRedirect = Boolean(
    !isSignedIn && !wasSignedIn && hasAttemptedFetch && pathname && !shouldNotRedirectRoutes.includes(pathname)
  );

  const signInLink = useLocalizedSignInLink();

  React.useEffect(() => {
    if (SENTRY_DSN && user?.id) {
      setUser({ id: user.id.toString() });
    }
  }, [user?.id]);

  React.useEffect(() => {
    if (shouldRedirect && !isLoading && !maintenanceModeEnabled) {
      if (siteDataBlocked) {
        return;
      }
      window.location.href = signInLink;
    }
  }, [isLoading, shouldRedirect, pathname, signInLink, siteDataBlocked]);

  React.useEffect(() => {
    if (isError) {
      errorToast(t('something_went_wrong_retry'));
    }
  }, [isError, t]);

  if (maintenanceModeEnabled) {
    return <MaintenanceMode />;
  }

  if (isAuthenticatePage) {
    return <>{children}</>;
  }

  // Keep the user on Connect (with the root warning banner visible) instead of
  // bouncing them to login when cookies/storage cannot persist anyway.
  if (siteDataBlocked && shouldRedirect && !isSignedIn) {
    return null;
  }

  if (isLoading || shouldRedirect) {
    return <FullPageLoader />;
  }

  if (isSignedIn || wasSignedIn) {
    return (
      <OrganizationProvider>
        <UploadToast />
        <Analytics />
        <AmplitudeEngagement />
        <OrganizationAccessGuard>{children}</OrganizationAccessGuard>
      </OrganizationProvider>
    );
  }

  return <>{children}</>;
}
